import { Activity, Settings, Wrench } from "lucide-react";

// ---------------------------------------------------------------------------
// Das System-Menue (/system, Superadmin) — Daten ohne Panels.
// ---------------------------------------------------------------------------
// Bis NAV-4 lag diese Liste in `SystemShell.tsx`, zusammen mit den Panels.
// Die Palette (⌘K) braucht die Menue-Woerter, um «System · Tokens» anbieten
// zu koennen — haette sie SystemShell importiert, waeren alle System-Panels
// mit in den Hauptbundle gewandert (/system ist ein Lazy-Chunk). Darum lebt
// das Menue hier, leicht; SystemShell und die Palette lesen beide dieselbe
// Liste (Prinzip 1).
//
// Drei Top-Items: Cockpit (Default) · Operations · Konfiguration.
// Ops + Config haben Sub-Tabs als zweite Zeile. URLs sind hierarchisch:
//   /system/                       → Cockpit
//   /system/cockpit                → Cockpit (Alias)
//   /system/ops/triggers           → Triggers (Cron-Run, Diagnostics, Self-Tests)
//   /system/ops/audit              → Audit-Log + Runtime-Logs
//   /system/ops/tools              → CustomerExtract, StepBackfill, Outbox
//   /system/ops/feedback           → «Problem melden»-Meldungen
//   /system/config/tokens          → bexio/Graph/Grok/Pingen-Tokens
//   /system/config/agb             → AGB-Versionen
//   /system/config/lead-spam       → Lead-Spam-Config
//   /system/config/integrations    → OneDrive/Bexio/Sentry/Graph-Cards
//
// Alte flache URLs (/system/tokens, /system/jobs, ...) werden in App.tsx
// per Navigate auf die neue Hierarchie umgeleitet — Bookmarks bleiben gueltig.

export type SystemTopId = "cockpit" | "ops" | "config";

export interface SystemSubTab {
  readonly value: string;
  readonly label: string;
  readonly path: string;
  // Eine Zeile fuer die Palette: was liegt hinter dem Tab.
  readonly description: string;
}

export interface SystemNavItem {
  readonly top: SystemTopId;
  readonly label: string;
  readonly icon: typeof Activity;
  readonly defaultPath: string;
  readonly subs: readonly SystemSubTab[];
}

export const SYSTEM_NAV: readonly SystemNavItem[] = [
  {
    top: "cockpit",
    label: "Cockpit",
    icon: Activity,
    defaultPath: "/system/cockpit",
    subs: [],
  },
  {
    top: "ops",
    label: "Operations",
    icon: Wrench,
    defaultPath: "/system/ops/triggers",
    subs: [
      {
        value: "triggers",
        label: "Triggers",
        path: "/system/ops/triggers",
        description: "Operations — Cron-Läufe, Diagnose und Self-Tests von Hand auslösen",
      },
      {
        value: "audit",
        label: "Audit",
        path: "/system/ops/audit",
        description: "Operations — Audit-Spur und Laufzeit-Logs",
      },
      {
        value: "tools",
        label: "Tools",
        path: "/system/ops/tools",
        description: "Operations — Kunden-Extrakt, Schritt-Backfill, Outbox",
      },
      {
        value: "feedback",
        label: "Feedback",
        path: "/system/ops/feedback",
        description: "Operations — «Problem melden»-Meldungen, offen zuerst",
      },
    ],
  },
  {
    top: "config",
    label: "Konfiguration",
    icon: Settings,
    defaultPath: "/system/config/tokens",
    subs: [
      {
        value: "tokens",
        label: "Tokens",
        path: "/system/config/tokens",
        description: "Konfiguration — bexio-, Graph-, Grok- und Pingen-Tokens",
      },
      {
        value: "agb",
        label: "AGB",
        path: "/system/config/agb",
        description: "Konfiguration — AGB-Versionen",
      },
      {
        value: "lead-spam",
        label: "Lead-Spam",
        path: "/system/config/lead-spam",
        description: "Konfiguration — Spam-Regeln für eingehende Leads",
      },
      {
        value: "integrations",
        label: "Integrationen",
        path: "/system/config/integrations",
        description: "Konfiguration — OneDrive, bexio, Sentry, Graph",
      },
    ],
  },
];

// Aliase fuer die alten flachen URLs — falls jemand die Routes umgeht
// (Direkt-Goto im Test) und wir trotzdem das richtige Tab-Highlighting
// brauchen. Die ECHTE Redirect-Logik sitzt in App.tsx via <Navigate>.
const ALIASES: Record<string, { top: SystemTopId; sub?: string }> = {
  "/system/tokens": { top: "config", sub: "tokens" },
  "/system/agb": { top: "config", sub: "agb" },
  "/system/lead-spam": { top: "config", sub: "lead-spam" },
  "/system/audit": { top: "ops", sub: "audit" },
  "/system/logs": { top: "ops", sub: "audit" },
  "/system/jobs": { top: "ops", sub: "triggers" },
  "/system/sync": { top: "ops", sub: "triggers" },
  "/system/selftests": { top: "ops", sub: "triggers" },
  "/system/customer-extract": { top: "ops", sub: "tools" },
  "/system/step-backfill": { top: "ops", sub: "tools" },
  "/system/outbox": { top: "ops", sub: "tools" },
};

export function resolveSystemRoute(pathname: string): {
  top: SystemTopId;
  sub?: string;
} {
  for (const [alias, target] of Object.entries(ALIASES)) {
    if (pathname === alias || pathname.startsWith(alias + "/")) return target;
  }
  for (const item of SYSTEM_NAV) {
    for (const sub of item.subs) {
      if (pathname === sub.path || pathname.startsWith(sub.path + "/")) {
        return { top: item.top, sub: sub.value };
      }
    }
    if (
      item.subs.length === 0 &&
      (pathname === item.defaultPath ||
        pathname.startsWith(item.defaultPath + "/"))
    ) {
      return { top: item.top };
    }
  }
  return { top: "cockpit" };
}
